import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { gymBillingTrialDaysLeft } from '../utils/gymBillingTrial';

const GymBillingContext = createContext(null);

/** Route (dopo /gym/) soggette a blur se trial scaduto / piano non attivo */
export const GYM_BLURRED_ROUTES = [
    'statistics',
    'statistics-individual',
    'bulk-enrollment',
    'import-csv',
    'add-member',
    'exercises',
    'social',
    'email',
    'calendar',
    'courses',
    'templates',
    'template-groups',
];

export function buildBillingFromAuthUser(user) {
    if (!user) {
        return null;
    }
    const status = user.gym_billing_status || 'active';
    const trialEndsAt = user.gym_trial_ends_at || null;
    const planActive = user.gym_plan_active !== 0 && user.gym_plan_active !== false;

    let daysLeft = null;
    let trialExpired = false;
    if (status === 'trialing' && trialEndsAt) {
        const trialDays = gymBillingTrialDaysLeft(trialEndsAt);
        daysLeft = trialDays.daysLeft;
        trialExpired = trialDays.trialExpired;
    }

    const blurFeatures =
        !planActive
        || status === 'expired'
        || (status === 'trialing' && trialExpired);

    return {
        gym_id: user.gym_id,
        plan: user.gym_plan || 'premium',
        plan_active: planActive,
        billing_status: status,
        trial_ends_at: trialEndsAt,
        days_left: daysLeft,
        trial_expired: trialExpired,
        blur_features: blurFeatures,
        blurred_routes: blurFeatures ? [...GYM_BLURRED_ROUTES] : [],
        stripe_enabled: false,
        _from_auth_fallback: true,
    };
}

function mergeBilling(apiData, authUser) {
    if (apiData && typeof apiData.blur_features === 'boolean') {
        return apiData;
    }
    const fallback = buildBillingFromAuthUser(authUser);
    if (!fallback) {
        return apiData || null;
    }
    return apiData ? { ...fallback, ...apiData } : fallback;
}

export function GymBillingProvider({ children }) {
    const { getAuthenticatedApi, isGym, isTrainer, currentUser } = useAuth();
    const [billing, setBilling] = useState(null);
    const [memberPayments, setMemberPayments] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const enabled = isGym() || isTrainer();

    const refreshBilling = useCallback(async () => {
        if (!enabled) {
            setBilling(null);
            setLoading(false);
            return null;
        }
        try {
            setLoading(true);
            const api = getAuthenticatedApi();
            const res = await api.get('/gym_billing.php', { params: { action: 'status' } });
            const merged = mergeBilling(res.data, currentUser);
            setBilling(merged);
            setError(null);
            return merged;
        } catch (err) {
            console.error('gym billing status:', err);
            const fallback = mergeBilling(null, currentUser);
            setError(err.response?.data?.error || err.message);
            setBilling(fallback);
            return fallback;
        } finally {
            setLoading(false);
        }
    }, [enabled, getAuthenticatedApi, currentUser]);

    const refreshMemberPayments = useCallback(async () => {
        if (!isGym()) {
            setMemberPayments(null);
            return null;
        }
        try {
            const api = getAuthenticatedApi();
            const res = await api.get('/gym_billing.php', {
                params: { action: 'member_payments_summary' },
            });
            setMemberPayments(res.data);
            return res.data;
        } catch (err) {
            console.error('member payments summary:', err);
            setMemberPayments(null);
            return null;
        }
    }, [isGym, getAuthenticatedApi]);

    useEffect(() => {
        refreshBilling();
    }, [refreshBilling, currentUser?.id, currentUser?.gym_id]);

    const isRouteBlurred = useCallback(
        (pathname) => {
            if (!billing?.blur_features || !billing?.blurred_routes?.length) {
                return false;
            }
            const path = pathname.replace(/^\/gym\/?/, '').replace(/\/$/, '');
            return billing.blurred_routes.some(
                (route) => path === route || path.startsWith(`${route}/`)
            );
        },
        [billing]
    );

    const isBlurrableRoute = useCallback((routeKey) => {
        const path = (routeKey || '').replace(/^\/gym\/?/, '').replace(/\/$/, '');
        return GYM_BLURRED_ROUTES.some(
            (route) => path === route || path.startsWith(`${route}/`)
        );
    }, []);

    const value = useMemo(
        () => ({
            billing,
            memberPayments,
            loading,
            error,
            refreshBilling,
            refreshMemberPayments,
            isRouteBlurred,
            isBlurrableRoute,
            isTrialing: billing?.billing_status === 'trialing' && !billing?.trial_expired,
            isTrialExpired: billing?.trial_expired === true || billing?.billing_status === 'expired',
            isComplimentary: billing?.billing_status === 'complimentary',
            stripeEnabled: billing?.stripe_enabled === true,
            planAllowedPaths: billing?.plan_allowed_paths ?? null,
        }),
        [
            billing,
            memberPayments,
            loading,
            error,
            refreshBilling,
            refreshMemberPayments,
            isRouteBlurred,
            isBlurrableRoute,
        ]
    );

    return (
        <GymBillingContext.Provider value={value}>
            {children}
        </GymBillingContext.Provider>
    );
}

export function useGymBilling() {
    const ctx = useContext(GymBillingContext);
    if (!ctx) {
        throw new Error('useGymBilling must be used within GymBillingProvider');
    }
    return ctx;
}

export function useGymBillingOptional() {
    return useContext(GymBillingContext);
}
